import { FC, useEffect, useState } from "react";
import {
  Box,
  Link,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import { EssentialImage, SwapContentLayout } from "components";
import { TERMS_OF_USE_URL } from "constants/index";
import { ACCEPTING_OFFERS_STATES } from "services/listings/constants";
import { Asset } from "services/listings/types";
import { ListingValuation, ListingWithIncludes } from "services/listings/types";
import { VALUATION_BREAKDOWN_KINDS_TO_DESCRIPTION_KINDS } from "services/valuation/constants";
import {
  formatValuationReportData,
  getReportOnTopSongs,
  getReportTotalEarnings,
} from "services/valuation/valuationPostProcessor";
import { ValuationDescription } from "services/valuationDescriptions/types";
import theme from "theme";
import {
  assetDetailsTabHeight,
  desktopHeaderHeight,
  headerHideTransition,
} from "theme/constants";

import ParagraphContent from "../ParagraphContent";
import EarningsByPeriod from "./EarningsByPeriod";
import TopEarningsByYear from "./TopEarningsByYear";
import ValuationCharts from "./ValuationCharts";

const useStyles = makeStyles<
  Theme,
  { isModal: boolean; scrollDirection: string }
>(() => ({
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "1.625rem",
    textAlign: "center",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("xs")]: {
      textAlign: "left",
    },
  },
  titleContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  center: {
    textAlign: "center",
  },
  genericFinancialsWrapper: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "600px",
  },
  customEarningsByPeriodChartWrapper: {
    padding: theme.spacing(5),
    backgroundColor: theme.custom.palette.gray.light,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
      textAlign: "left",
    },
  },
  historicalEarningsNotice: {
    "& p": {
      fontSize: "0.875rem",
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1),
      },
    },
    "& a": {
      color: theme.custom.palette.blue.main,
      fontSize: "0.875rem",
    },
    [theme.breakpoints.up("xs")]: {
      textAlign: "left",
    },
  },
  contentWrapper: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  outerChartWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "sticky",
    top: props =>
      props.scrollDirection === "up" && !props.isModal
        ? desktopHeaderHeight + assetDetailsTabHeight + 16
        : assetDetailsTabHeight + 16,
    transition: headerHideTransition,
  },
  chartWrapper: {
    height: "100%",
    width: "100%",
    maxWidth: "500px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginBottom: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    },
  },
  royaltyPayorBarChart: {
    height: "500px",
  },
}));

const VALUATION_BREAKDOWN_KINDS = [
  "top_songs",
  "top_income_types",
  "top_sources",
  "top_music_users",
] as const;

interface FinancialsProps {
  asset: Asset;
  isModal: boolean;
  listing: ListingWithIncludes | undefined;
  valuation: ListingValuation;
  valuationDescription: ValuationDescription | null;
}

const Financials: FC<FinancialsProps> = ({
  asset,
  isModal,
  listing,
  valuation,
  valuationDescription,
}) => {
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    distribution_frequency,
    earnings_by_region,
    top_income_types,
    top_music_users,
    top_sources,
    top_songs,
    top_earnings_by_year,
  } = valuation;

  const { generic_financials, custom_earnings_by_region_chart, state } =
    listing || {};

  const { mismatch_notice, royalty_payors } = asset;

  const VALUATION_BREAKDOWN_KINDS_TO_CONTENT_DATA = {
    top_songs: {
      title: "Top 5 Track Breakdown",
      sources: top_songs ? getReportOnTopSongs(top_songs) : [],
      totalValue: top_songs ? getReportTotalEarnings(top_songs) : 0,
      chartTitle: "Tracks",
      contentKind: VALUATION_BREAKDOWN_KINDS_TO_DESCRIPTION_KINDS.top_songs,
      barChartData: top_earnings_by_year?.top_songs,
    },
    top_income_types: {
      title: "Top 5 Income Type Breakdown",
      sources: top_income_types
        ? formatValuationReportData(top_income_types)
        : [],
      totalValue: top_income_types
        ? getReportTotalEarnings(top_income_types)
        : 0,
      chartTitle: "Income Types",
      contentKind:
        VALUATION_BREAKDOWN_KINDS_TO_DESCRIPTION_KINDS.top_income_types,
      barChartData: top_earnings_by_year?.top_income_types,
    },
    top_sources: {
      title: "Top 5 Sources Breakdown",
      sources: top_sources ? formatValuationReportData(top_sources) : [],
      totalValue: top_sources ? getReportTotalEarnings(top_sources) : 0,
      chartTitle: "Sources",
      contentKind: VALUATION_BREAKDOWN_KINDS_TO_DESCRIPTION_KINDS.top_sources,
      barChartData: top_earnings_by_year?.top_sources,
    },
    top_music_users: {
      title: "Top 5 Music User Breakdown",
      sources: top_music_users
        ? formatValuationReportData(top_music_users)
        : [],
      totalValue: top_music_users ? getReportTotalEarnings(top_music_users) : 0,
      chartTitle: "Music Users",
      contentKind:
        VALUATION_BREAKDOWN_KINDS_TO_DESCRIPTION_KINDS.top_music_users,
      barChartData: top_earnings_by_year?.top_music_users,
    },
  };
  const visibleFinancialSections = VALUATION_BREAKDOWN_KINDS.filter(
    kind => !!VALUATION_BREAKDOWN_KINDS_TO_CONTENT_DATA[kind].sources.length,
  );
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [scrollDirection, setScrollDirection] = useState("up");

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos =
        window.scrollY || document.documentElement.scrollTop;

      if (currentScrollPos > lastScrollTop) {
        // Scrolling down
        setScrollDirection("down");
      } else {
        // Scrolling up
        setScrollDirection("up");
      }

      setLastScrollTop(currentScrollPos <= 0 ? 0 : currentScrollPos); // For Mobile or negative scrolling
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollTop]);

  const classes = useStyles({ isModal, scrollDirection });

  return (
    <Box data-testid="financials">
      {!!generic_financials ? (
        <Box className={classes.genericFinancialsWrapper}>
          <EssentialImage htmlString={generic_financials} />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(5)}>
          {!!top_earnings_by_year?.top_royalty_payors && (
            <Box className={classes.customEarningsByPeriodChartWrapper}>
              <Box className={classes.royaltyPayorBarChart}>
                <TopEarningsByYear
                  title="Royalty Payors"
                  data={top_earnings_by_year?.top_royalty_payors}
                  titleizeKey={false}
                />
              </Box>
            </Box>
          )}
          {!!earnings_by_region && (
            <Box className={classes.customEarningsByPeriodChartWrapper}>
              <EarningsByPeriod
                earningsByPeriod={earnings_by_region}
                distributionFrequency={distribution_frequency}
                royaltyPayors={royalty_payors}
                mismatchNotice={mismatch_notice}
                isListingOpenForOffers={ACCEPTING_OFFERS_STATES.includes(state)}
              />
            </Box>
          )}
          {!!custom_earnings_by_region_chart && (
            <Box className={classes.customEarningsByPeriodChartWrapper}>
              <EssentialImage htmlString={custom_earnings_by_region_chart} />
            </Box>
          )}
          {visibleFinancialSections.map((kind, index) => {
            const contentData = VALUATION_BREAKDOWN_KINDS_TO_CONTENT_DATA[kind];
            const valuationDescriptionContent = valuationDescription?.contents.find(
              content => content.kind === contentData.contentKind,
            );

            return (
              <SwapContentLayout
                key={kind}
                primary={
                  <Box className={classes.outerChartWrapper}>
                    <Box className={classes.chartWrapper}>
                      {isSmDown && (
                        <Typography component="h1" className={classes.title}>
                          {contentData.title}
                        </Typography>
                      )}
                      <ValuationCharts
                        sources={contentData.sources}
                        title={contentData.chartTitle}
                        totalValue={contentData.totalValue}
                        kind={kind}
                        barChartData={contentData.barChartData}
                      />
                    </Box>
                  </Box>
                }
                secondary={
                  <Box className={classes.contentWrapper}>
                    {valuationDescriptionContent ? (
                      <Box>
                        {!isSmDown && (
                          <Typography component="h1" className={classes.title}>
                            {contentData.title}
                          </Typography>
                        )}
                        <ParagraphContent>
                          <EssentialImage
                            htmlString={valuationDescriptionContent.body}
                          />
                        </ParagraphContent>
                      </Box>
                    ) : !isSmDown ? (
                      <Box className={classes.title}>{contentData.title}</Box>
                    ) : null}
                  </Box>
                }
                swap={isSmDown ? false : index % 2 === 1}
              />
            );
          })}
        </Box>
      )}
      <Box mt={2} className={classes.historicalEarningsNotice}>
        <Typography>
          * Historic royalty income does not guarantee future royalty income.
          Future royalty income depends upon future sales and licensing revenue
          generated by the intellectual property associated with this listing.
          All earnings analysis and investment decisions are investors’ sole
          responsibility per Royalty Exchange’s{" "}
          <Link variant="inherit" href={TERMS_OF_USE_URL} target="_blank">
            Terms of Use
          </Link>
          .
        </Typography>
      </Box>
    </Box>
  );
};

export default Financials;
